import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Alert
} from '@mui/material';
import {
  Chat as ChatIcon,
  CloudUpload as UploadIcon,
  AccountTree as WorkflowIcon,
  FeaturedPlayList as FeaturesIcon,
  Person as PersonIcon,
  Groups as UserRolesIcon,
  Share as InputSourcesIcon,
  SmartToy as AgentsIcon,
  Tune as FinetuningIcon,
  Schedule as FrequencyIcon,
  Output as OutputIcon,
  DesignServices as UIUXIcon,
  Build as ToolsIcon,
  MoreVert as MoreVertIcon,
  AutoAwesome as AIIcon,
  Description as DocumentIcon,
  Psychology as AnalysisIcon
} from '@mui/icons-material';

/**
 * AgenticCrewWorkflow Component
 * Implements agentic crew workflow based on Anthropic's effective agents guidelines
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project ID
 * @param {Object} props.project - Project data
 * @param {Function} props.onTaskClick - Callback for task click
 * @param {Function} props.onMenuOpen - Callback for menu open
 * @param {Function} props.showNotification - Function to show notifications
 * @returns {React.ReactElement} AgenticCrewWorkflow component
 */
const AgenticCrewWorkflow = ({
  projectId,
  project,
  onTaskClick,
  onMenuOpen,
  showNotification
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for initialization dialog
  const [showInitDialog, setShowInitDialog] = useState(false);
  const [selectedInitMethod, setSelectedInitMethod] = useState(null);

  // Define agentic crew workflow structure following Anthropic guidelines
  const agenticWorkflow = {
    // Entry point - similar to project info but for agentic crews
    crewInfo: {
      id: 'agentic-crew-info',
      title: 'Agentic Crew Info',
      icon: <AIIcon />,
      description: 'Define your agentic crew scope and initialization method',
      section: 'Initialization',
      color: '#1976d2',
      isEntry: true
    },

    // Three ways to start (as specified)
    initializationMethods: [
      {
        id: 'upload-document',
        title: 'Upload Document',
        icon: <UploadIcon />,
        description: 'Upload documents to define crew instructions and context',
        section: 'Initialization'
      },
      {
        id: 'chat-with-ailyce',
        title: 'Chat with Ailyce',
        icon: <ChatIcon />,
        description: 'Explain your needs through conversation with Ailyce',
        section: 'Initialization'
      },
      {
        id: 'upload-workflow',
        title: 'Upload/Create Workflow',
        icon: <WorkflowIcon />,
        description: 'Define or upload existing workflow templates',
        section: 'Initialization'
      }
    ],

    // Analysis tiles (core agentic crew configuration)
    analysisTiles: [
      {
        id: 'features',
        title: 'Features',
        icon: <FeaturesIcon />,
        description: 'Define core features and capabilities the crew will handle',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'user-stories',
        title: 'User Stories',
        icon: <PersonIcon />,
        description: 'Create user stories for crew interactions and outcomes',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'user-roles',
        title: 'User Roles',
        icon: <UserRolesIcon />,
        description: 'Define user roles and permissions when crew is deployed',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'process-flows',
        title: 'Process Flows',
        icon: <WorkflowIcon />,
        description: 'Map out agent decision-making and workflow patterns',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'input-sources',
        title: 'Input Sources',
        icon: <InputSourcesIcon />,
        description: 'Define where the crew gets its input data and triggers',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'agents-orchestrators',
        title: 'Agents & Orchestrators',
        icon: <AgentsIcon />,
        description: 'Design agent roles and orchestration patterns',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'finetuning-activities',
        title: 'Finetuning Activities',
        icon: <FinetuningIcon />,
        description: 'Configure agent learning and optimization activities',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'frequency',
        title: 'Frequency',
        icon: <FrequencyIcon />,
        description: 'Set execution frequency and scheduling patterns',
        section: 'Analysis',
        color: '#388e3c'
      },
      {
        id: 'output',
        title: 'Output',
        icon: <OutputIcon />,
        description: 'Define output format: email, knowledge hub, dashboard, etc.',
        section: 'Analysis',
        color: '#388e3c'
      }
    ],

    // Conditional UI/UX section
    uiuxTiles: [
      {
        id: 'ui-design',
        title: 'UI/UX Design',
        icon: <UIUXIcon />,
        description: 'Design interface when output requires an application',
        section: 'UI/UX',
        color: '#7b1fa2',
        conditional: true
      }
    ],

    // Technical implementation
    technicalTiles: [
      {
        id: 'tools-integration',
        title: 'Tools & Integration',
        icon: <ToolsIcon />,
        description: 'Configure tools like web search, APIs, and integrations',
        section: 'Technical',
        color: '#f57c00'
      }
    ]
  };

  // Handle crew info click (entry point)
  const handleCrewInfoClick = useCallback(() => {
    setShowInitDialog(true);
  }, []);

  // Handle initialization method selection
  const handleInitMethodSelect = useCallback((method) => {
    setSelectedInitMethod(method);
    setShowInitDialog(false);
    
    // Navigate to appropriate initialization flow
    switch (method.id) {
      case 'upload-document':
        // Navigate to document upload interface
        window.location.href = `/projects/${projectId}/crew-upload`;
        break;
      case 'chat-with-ailyce':
        // Navigate to conversation with crew context
        window.location.href = `/projects/${projectId}/conversation?task=agentic-crew-chat&crew=true`;
        break;
      case 'upload-workflow':
        // Navigate to workflow builder
        window.location.href = `/projects/${projectId}/crew-workflow-builder`;
        break;
      default:
        break;
    }
    
    showNotification({
      message: `Starting crew setup with: ${method.title}`,
      type: 'info'
    });
  }, [projectId, showNotification]);

  // Handle regular task click
  const handleTaskClick = useCallback((task) => {
    if (task.isEntry) {
      handleCrewInfoClick();
      return;
    }

    // Handle special navigation for tools integration
    if (task.id === 'tools-integration' || task.id === 'agentic-tools-integration') {
      window.location.href = `/projects/${projectId}/mcp-integration`;
      return;
    }

    // Handle special navigation for MCP integration
    if (task.id === 'mcp-integration' || task.id === 'agentic-mcp-integration') {
      window.location.href = `/projects/${projectId}/mcp-integration`;
      return;
    }

    if (onTaskClick) {
      onTaskClick(task);
    } else {
      // Navigate to conversation with task context
      const url = `/projects/${projectId}/conversation?task=${task.id}&crew=true`;
      window.location.href = url;
    }
  }, [projectId, onTaskClick, handleCrewInfoClick]);

  // Handle menu open
  const handleMenuOpen = useCallback((event, task) => {
    event.stopPropagation();
    if (onMenuOpen) {
      onMenuOpen(event, task);
    }
  }, [onMenuOpen]);

  // Get task status (mock implementation)
  const getTaskStatus = (task) => {
    // Mock status based on task ID
    const statuses = ['pending', 'in_progress', 'completed'];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    
    switch (status) {
      case 'completed':
        return { color: 'success', label: 'Complete' };
      case 'in_progress':
        return { color: 'warning', label: 'In Progress' };
      default:
        return { color: 'default', label: 'Pending' };
    }
  };

  // Render task card
  const renderTaskCard = (task, index) => {
    const taskStatus = getTaskStatus(task);
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={task.id}>
        <Fade in={true} timeout={300 + (index * 100)}>
          <Card 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backgroundColor: task.color ? `${task.color}10` : '#f5f5f5',
              border: `1px solid ${task.color || '#e0e0e0'}20`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.shadows[8],
                backgroundColor: task.color ? `${task.color}20` : '#f0f0f0'
              }
            }}
          >
            <CardActionArea 
              onClick={() => handleTaskClick(task)}
              sx={{ height: '100%' }}
            >
              <CardContent sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative',
                pb: 3
              }}>
                {/* Menu Button */}
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    opacity: 0.7,
                    '&:hover': { opacity: 1 }
                  }}
                  onClick={(e) => handleMenuOpen(e, task)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>

                {/* Status Chip */}
                {!task.isEntry && (
                  <Chip 
                    label={taskStatus.label}
                    color={taskStatus.color}
                    size="small"
                    sx={{ 
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      fontSize: '0.75rem'
                    }}
                  />
                )}

                {/* Task Icon */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: task.isEntry ? 2 : 4, 
                  mb: 2 
                }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '50%',
                    backgroundColor: task.color ? `${task.color}20` : '#e3f2fd',
                    color: task.color || '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(task.icon, { fontSize: 'large' })}
                  </Box>
                </Box>

                {/* Task Title */}
                <Typography 
                  variant="h6" 
                  component="h3"
                  sx={{ 
                    fontWeight: 600,
                    color: task.color || '#1976d2',
                    textAlign: 'center',
                    mb: 1,
                    fontSize: task.isEntry ? '1.2rem' : '1.1rem'
                  }}
                >
                  {task.title}
                </Typography>

                {/* Entry point badge */}
                {task.isEntry && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Chip 
                      label="Start Here" 
                      color="primary" 
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                )}

                {/* Task Description */}
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ 
                    textAlign: 'center',
                    flexGrow: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontSize: '0.875rem'
                  }}
                >
                  {task.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Fade>
      </Grid>
    );
  };

  // Render section
  const renderSection = (title, tasks, icon) => (
    <Box sx={{ mb: 6 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
        pb: 2,
        borderBottom: '2px solid #e0e0e0'
      }}>
        {icon}
        <Typography 
          variant="h5" 
          component="h2"
          sx={{ 
            fontWeight: 600,
            color: '#1976d2',
            ml: 2,
            fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif"
          }}
        >
          {title}
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {tasks.map((task, index) => renderTaskCard(task, index))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: '#1976d2',
            mb: 1,
            fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif"
          }}
        >
          🤖 Agentic Crew Workflow
        </Typography>
        <Typography 
          variant="body1" 
          color="textSecondary"
          sx={{ maxWidth: 800, mx: 'auto', mb: 2 }}
        >
          Build effective AI agents following Anthropic's best practices. Start simple, 
          then add complexity only when it demonstrably improves outcomes.
        </Typography>
        
        {/* Anthropic Guidelines Alert */}
        <Alert severity="info" sx={{ maxWidth: 800, mx: 'auto', textAlign: 'left' }}>
          <Typography variant="body2">
            <strong>Following Anthropic Guidelines:</strong> This workflow emphasizes simplicity, 
            transparency, and composable patterns. Start with the Crew Info tile to begin your agent design.
          </Typography>
        </Alert>
      </Box>

      {/* Entry Point */}
      {renderSection('🚀 Start Here', [agenticWorkflow.crewInfo], <AIIcon sx={{ color: '#1976d2' }} />)}

      {/* Analysis Section */}
      {renderSection('🔍 Analysis & Configuration', agenticWorkflow.analysisTiles, <AnalysisIcon sx={{ color: '#388e3c' }} />)}

      {/* Conditional UI/UX Section */}
      {renderSection('🎨 UI/UX (When Output Requires Application)', agenticWorkflow.uiuxTiles, <UIUXIcon sx={{ color: '#7b1fa2' }} />)}

      {/* Technical Section */}
      {renderSection('⚙️ Technical Implementation', agenticWorkflow.technicalTiles, <ToolsIcon sx={{ color: '#f57c00' }} />)}

      {/* Initialization Method Dialog */}
      <Dialog 
        open={showInitDialog} 
        onClose={() => setShowInitDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            🚀 Choose Your Initialization Method
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Select how you'd like to start building your agentic crew:
          </Typography>
        </DialogTitle>
        <DialogContent>
          <List>
            {agenticWorkflow.initializationMethods.map((method) => (
              <React.Fragment key={method.id}>
                <ListItem 
                  button
                  onClick={() => handleInitMethodSelect(method)}
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    mb: 2,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#1976d2'
                    }
                  }}
                >
                  <ListItemIcon>
                    {method.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={method.title}
                    secondary={method.description}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInitDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

AgenticCrewWorkflow.propTypes = {
  projectId: PropTypes.string.isRequired,
  project: PropTypes.object,
  onTaskClick: PropTypes.func,
  onMenuOpen: PropTypes.func,
  showNotification: PropTypes.func
};

export default AgenticCrewWorkflow;